import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { TrendingUp, DollarSign, CheckCircle, XCircle, Loader2, AlertTriangle, Target } from 'lucide-react';
import YearSelector from './YearSelector';
import { useYear } from '../contexts/YearContext';
import Onboarding from './Onboarding';
import { invalidateAccountBalances, subscribeToBalanceInvalidation } from '../hooks/useAccountBalances';
import { HelpSection, HelpP, HelpList, HelpCallout } from './HelpSection';
import SystemHealthPill from './SystemHealthPill';
import MoneyFlowExplainer from './MoneyFlowExplainer';
import RebalanceModal from './RebalanceModal';
import IncomeTrendChart from './IncomeTrendChart';
import PlanHealthCard from './PlanHealthCard';
import CashflowGamePlanCard from './CashflowGamePlanCard';

function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const { selectedYear } = useYear();
  
  // Rebalance state
  const [requiredIncome, setRequiredIncome] = useState(null);
  const [rebalanceData, setRebalanceData] = useState(null);
  const [showRebalanceModal, setShowRebalanceModal] = useState(false);
  const [rebalanceMode, setRebalanceMode] = useState('instant'); // 'instant' or 'catchup'
  const [catchupWeeks, setCatchupWeeks] = useState(4);

  // Weekly Target widget mode — Survival / True Minimum / Catch-Up.
  // Default = 'growth' so the headline surfaces the Financial Plan's
  // True Minimum Income (what you need to keep the plan fully funded),
  // matching the Plan Setup "True Minimum Income" card exactly.
  const [targetMode, setTargetMode] = useState('growth');

  useEffect(() => {
    if (selectedYear) {
      loadDashboard();
      checkOnboarding();
    }
  }, [selectedYear]);

  // Deep-link: Fin AI (or other surfaces) may route here with ?open=rebalance
  // to jump straight into the Rebalance Plan modal once data is ready.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('open') === 'rebalance' && rebalanceData) {
      setShowRebalanceModal(true);
      // Clean the URL so it doesn't re-trigger on the next navigation.
      params.delete('open');
      const qs = params.toString();
      const newUrl = window.location.pathname + (qs ? `?${qs}` : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, [rebalanceData]);

  // Subscribe the Dashboard to the shared balance-invalidation bus so
  // writes made elsewhere refresh this view immediately. For optimistic
  // mutations, we patch local summary state inline to feel instant; for hard
  // invalidations, we re-run loadDashboard() to pick up server truth.
  useEffect(() => {
    const unsubscribe = subscribeToBalanceInvalidation((payload = {}) => {
      if (payload.type === 'optimistic' && payload.delta) {
        setSummary((prev) => {
          if (!prev || !prev.current_balances) return prev;
          const cb = prev.current_balances;
          return {
            ...prev,
            current_balances: {
              ...cb,
              operations:      Number(((cb.operations || 0)      + (payload.delta.operations      || 0)).toFixed(2)),
              business_growth: Number(((cb.business_growth || 0) + (payload.delta.business_growth || 0)).toFixed(2)),
              taxes:           Number(((cb.taxes || 0)           + (payload.delta.taxes           || 0)).toFixed(2)),
              savings:         Number(((cb.savings || 0)         + (payload.delta.savings         || 0)).toFixed(2)),
              buffer:          Number(((cb.buffer || 0)          + (payload.delta.buffer          || 0)).toFixed(2)),
            },
          };
        });
        return;
      }
      if (payload.type === 'rollback') {
        // Hard reload from server after rollback so Dashboard shows truth
        loadDashboard();
        return;
      }
      loadDashboard();
    });
    return unsubscribe;
    // eslint-disable-next-line
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/dashboard/summary?year=${selectedYear}`);
      setSummary(response.data);
      setError(null);
      
      // Load required income and rebalance data
      loadFinancialHealth();
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };
  
  const loadFinancialHealth = async (weeks = 4) => {
    try {
      const [incomeRes, rebalanceRes] = await Promise.all([
        axios.get(`${API}/settings/required-weekly-income`),
        axios.get(`${API}/settings/rebalance-suggestions?weeks=${weeks}&year=${selectedYear}`)
      ]);
      setRequiredIncome(incomeRes.data);
      setRebalanceData(rebalanceRes.data);
    } catch (err) {
      console.error('Error loading financial health:', err);
    }
  };
  
  // Reload rebalance data when weeks change
  const handleWeeksChange = async (newWeeks) => {
    setCatchupWeeks(newWeeks);
    try {
      const response = await axios.get(`${API}/settings/rebalance-suggestions?weeks=${newWeeks}&year=${selectedYear}`);
      setRebalanceData(response.data);
    } catch (err) {
      console.error('Error reloading rebalance data:', err);
    }
  };

  const checkOnboarding = async () => {
    try {
      const response = await axios.get(`${API}/user-info`);
      setUserInfo(response.data);
      
      // Show onboarding if user hasn't completed it
      if (!response.data.onboarding_completed) {
        setShowOnboarding(true);
      }
    } catch (err) {
      console.error('Error checking onboarding status:', err);
    }
  };

  const handleOnboardingComplete = async () => {
    try {
      await axios.post(`${API}/user/complete-onboarding`);
      setShowOnboarding(false);
    } catch (err) {
      console.error('Error completing onboarding:', err);
      setShowOnboarding(false);
    }
  };

  const handleOnboardingSkip = async () => {
    try {
      await axios.post(`${API}/user/complete-onboarding`);
      setShowOnboarding(false);
    } catch (err) {
      console.error('Error skipping onboarding:', err);
      setShowOnboarding(false);
    }
  };

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800" data-testid="dashboard-error">
        {error}
      </div>
    );
  }

  const successRate = summary.weeks_logged > 0
    ? Math.round((Math.min(summary.business_req_met_count, summary.personal_req_met_count) / summary.weeks_logged) * 100)
    : 0;

  return (
    <>
      {showOnboarding && (
        <Onboarding 
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
          userRole={userInfo?.role}
        />
      )}
      
      <div className="space-y-6" data-testid="dashboard">
      {/* Compact header — title + status pill + year, nothing else.
          The verbose "How your system works" explainers moved into a
          collapsed accordion at the bottom of the page. */}
      <div className="flex items-center justify-between flex-wrap gap-3" data-testid="dashboard-header">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Your operating view · {selectedYear}</p>
        </div>
        <div className="flex items-center gap-3">
          <SystemHealthPill />
          <YearSelector />
        </div>
      </div>

      {/* Top row — two decision cards side-by-side: Weekly Target + Plan Health */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="dashboard-top-row">
        {requiredIncome && !requiredIncome.error && requiredIncome.targets ? (
          <WeeklyTargetCard
            data={requiredIncome}
            mode={targetMode}
            onModeChange={setTargetMode}
          />
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 flex items-center justify-center text-sm text-gray-400 dark:text-gray-500">
            Weekly Target unavailable
          </div>
        )}
        <PlanHealthCard />
      </div>

      {/* Income vs Buffer Trend — the middle chart row */}
      <IncomeTrendChart />

      {/* Compact inline Rebalance alert — shown ONLY when needed. Replaces
          the previous dominant orange banner. Renders just above Account
          Health so the user can act on it in context. */}
      {(rebalanceData?.instant_transfers?.length > 0 ||
        rebalanceData?.total_deficit > 0 ||
        rebalanceData?.cap_status?.over_cap) && (
        <div
          className={`flex items-center justify-between gap-3 px-4 py-3 rounded-lg border text-sm flex-wrap ${
            rebalanceData?.cap_status?.over_cap
              ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700 text-red-700 dark:text-red-300'
              : 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-300'
          }`}
          data-testid="rebalance-alert-compact"
        >
          <div className="flex items-center gap-2 min-w-0">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span className="truncate">
              <strong>Rebalance needed:</strong> ${Number(rebalanceData?.total_deficit || 0).toLocaleString()} imbalance
              {rebalanceData?.cap_status?.over_cap ? ' · Business over cap' : ''}
            </span>
          </div>
          <button
            onClick={() => setShowRebalanceModal(true)}
            className="font-semibold underline underline-offset-2 hover:no-underline whitespace-nowrap"
            data-testid="rebalance-btn"
          >
            Review Plan
          </button>
        </div>
      )}

      {/* Account Health — simplified bottom decision row */}
      {rebalanceData?.account_health && Object.keys(rebalanceData.account_health).length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6" data-testid="account-health">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Account Health</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Expected balances update as income and expenses are logged · Actual balances live in Settings → Balances · 🟢 On track · 🔴 Needs funding · 🟡 Overfunded
          </p>

          <div className="space-y-4">
            {['personal', 'business', 'taxes', 'savings', 'buffer'].map((account) => {
              const health = rebalanceData.account_health[account];
              if (!health) return null;

              const percentage = health.target > 0 ? Math.min((health.actual / health.target) * 100, 150) : 0;
              const isOver = health.status === 'overfunded';
              const isUnder = health.status === 'underfunded';

              return (
                <div key={account} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700 dark:text-gray-300 capitalize">{account}</span>
                    <div className="text-right">
                      <span className={`font-bold ${isOver ? 'text-green-600' : isUnder ? 'text-red-600' : 'text-gray-600 dark:text-gray-400'}`}>
                        ${health.actual.toLocaleString()}
                      </span>
                      <span className="text-gray-400 dark:text-gray-500 text-sm ml-2">
                        / ${health.target.toLocaleString()} target
                      </span>
                    </div>
                  </div>
                  <div className="relative h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`absolute left-0 top-0 h-full rounded-full transition-all ${
                        isOver ? 'bg-green-500' : isUnder ? 'bg-red-500' : 'bg-blue-500'
                      }`}
                      style={{ width: `${Math.min(percentage, 100)}%` }}
                    />
                    {percentage > 100 && (
                      <div
                        className="absolute top-0 h-full bg-green-300 opacity-50 rounded-r-full"
                        style={{ left: '100%', width: `${Math.min(percentage - 100, 50)}%` }}
                      />
                    )}
                    <div className="absolute top-0 bottom-0 w-0.5 bg-gray-800 dark:bg-white" style={{ left: 'calc(100% - 1px)' }} />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>{health.percentage}% allocation</span>
                    <span className={isOver ? 'text-green-600' : isUnder ? 'text-red-600' : ''}>
                      {health.difference > 0 ? '+' : ''}{health.difference.toLocaleString()}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Rebalance Plan Modal (extracted to RebalanceModal.js) */}
      <RebalanceModal
        open={showRebalanceModal && !!rebalanceData}
        onClose={() => setShowRebalanceModal(false)}
        rebalanceData={rebalanceData}
        selectedYear={selectedYear}
        catchupWeeks={catchupWeeks}
        onCatchupWeeksChange={handleWeeksChange}
        onApplied={() => { loadFinancialHealth(catchupWeeks); }}
      />

      {/* Cashflow Game Plan — deterministic planner + CFO advisor (collapsed) */}
      <CashflowGamePlanCard />

      {/* ─── Advanced sections — collapsed by default ─────────────────────
          These are useful details but not daily-dashboard essentials. The
          native <details>/<summary> element keeps the toggle behaviour
          state-free and fully accessible. */}

      <details className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden" data-testid="dash-advanced-metrics">
        <summary className="cursor-pointer px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/50 select-none">
          Advanced Metrics
          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">Total income YTD · Avg weekly · Weeks logged · Success rate</span>
        </summary>
        <div className="px-6 pb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Total Income YTD"
              value={`$${summary.total_income_ytd.toLocaleString()}`}
              icon={DollarSign}
              color="blue"
              testId="metric-income"
            />
            <MetricCard
              title="Avg Weekly Income"
              value={`$${summary.avg_weekly_income.toLocaleString()}`}
              icon={TrendingUp}
              color="green"
              testId="metric-avg-income"
            />
            <MetricCard
              title="Weeks Logged"
              value={summary.weeks_logged}
              icon={CheckCircle}
              color="purple"
              testId="metric-weeks"
            />
            <MetricCard
              title="Success Rate"
              value={`${successRate}%`}
              icon={successRate >= 80 ? CheckCircle : XCircle}
              color={successRate >= 80 ? 'green' : 'red'}
              testId="metric-success"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-2">Business Requirements</h3>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{summary.business_req_met_count}</p>
                  <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">of {summary.weeks_logged} weeks met</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <div className="mt-3 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all"
                  style={{ width: `${summary.weeks_logged > 0 ? (summary.business_req_met_count / summary.weeks_logged) * 100 : 0}%` }}
                  data-testid="business-req-progress"
                />
              </div>
            </div>
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-2">Personal Requirements</h3>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{summary.personal_req_met_count}</p>
                  <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">of {summary.weeks_logged} weeks met</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <div className="mt-3 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all"
                  style={{ width: `${summary.weeks_logged > 0 ? (summary.personal_req_met_count / summary.weeks_logged) * 100 : 0}%` }}
                  data-testid="personal-req-progress"
                />
              </div>
            </div>
          </div>
        </div>
      </details>

      <details className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden" data-testid="dash-ytd-allocations">
        <summary className="cursor-pointer px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/50 select-none">
          YTD Allocation Details
          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">Per-bucket totals + Account Balances grid</span>
        </summary>
        <div className="px-6 pb-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <PersonalAllocationCard
              label={`Personal Account (${summary.personal_pct ?? 55}%)`}
              total={summary.total_personal_ytd}
              surplus={summary.total_net_after_expenses}
              testId="allocation-personal"
            />
            <AllocationBar
              label={`Business Account (${summary.business_pct ?? 20}%)`}
              amount={summary.total_business_ytd}
              color="blue"
              testId="allocation-business"
            />
            <AllocationBar
              label={`Taxes Account (${summary.taxes_pct ?? 20}%)`}
              amount={summary.total_taxes_ytd}
              color="red"
              testId="allocation-taxes"
            />
            <AllocationBar
              label={`Savings Account (${summary.savings_pct ?? 5}%)`}
              amount={summary.total_savings_ytd}
              color="yellow"
              testId="allocation-savings"
            />
          </div>

          <div>
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-2">Account Balances</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Expected Current Balance (allocated − tagged expenses) · Bank reality lives in Settings → Balances as Actual Balances.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <AccountBalance title="Personal Account" amount={summary.current_balances.operations} description="Living expenses & bills" testId="balance-personal" />
              <AccountBalance title="Business Account" amount={summary.current_balances.business_growth} description="Marketing, leads & business costs" testId="balance-business" />
              <AccountBalance title="Taxes Account" amount={summary.current_balances.taxes} description="Tax reserve (allocated − tax payments)" testId="balance-taxes" />
              <AccountBalance title="Savings Account" amount={summary.current_balances.savings} description="Emergency fund & future goals" testId="balance-savings" />
              <AccountBalance title="Buffer Account" amount={summary.current_balances.buffer} description="Unallocated capital retained outside plan buckets" testId="balance-buffer" />
            </div>
          </div>
        </div>
      </details>

      <details className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden" data-testid="dash-how-it-works">
        <summary className="cursor-pointer px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/50 select-none">
          How Dashboard Works
          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">Expected vs Actual · Weekly Target modes · Money flow</span>
        </summary>
        <div className="px-6 pb-6 space-y-4">
          <MoneyFlowExplainer testId="money-flow-dashboard" />
          <HelpSection title="How Your Dashboard Works" testId="help-dashboard">
            <HelpP>This is your system at a glance.</HelpP>
            <HelpList
              items={[
                <><strong>Weekly Target</strong> → The income you need to hit each week to keep your plan fully funded (matches Financial Plan → <em>True Minimum Income</em>)</>,
                <><strong>Account Health</strong> → How each <em>Expected Current Balance</em> (allocated − expenses) compares to its target</>,
                <><strong>Account Balances</strong> → Two views: <em>Expected</em> (what the system math says) vs <em>Actual</em> (what you entered in Settings → Balances from your bank)</>,
              ]}
            />
            <HelpCallout tone="emerald">
              <strong>Simple rule:</strong> If you hit your weekly target, your system works. Everything else is optimization.
            </HelpCallout>
          </HelpSection>

          <HelpSection title="How Your Weekly Target Works" testId="help-weekly-target">
            <HelpP>This is the number your system runs on.</HelpP>
            <HelpP muted>It tells you how much you need to earn each week to cover personal expenses, business expenses, and keep all accounts funded.</HelpP>
            <p className="font-semibold text-gray-900 dark:text-gray-100 mt-1">Modes:</p>
            <HelpList
              items={[
                <><strong>Survival</strong> → Covers essential expenses only</>,
                <><strong>True Minimum</strong> → Minimum weekly income to keep the plan fully funded (canonical target)</>,
                <><strong>Catch-Up</strong> → Helps you recover if behind your income pace</>,
              ]}
            />
          </HelpSection>
        </div>
      </details>
    </div>
    </>
  );
}

function MetricCard({ title, value, icon: Icon, color, testId }) {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
    red: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 transition-colors" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</p>
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
    </div>
  );
}

function AccountBalance({ title, amount, description, testId }) {
  const over = amount < 0;
  const absAmount = Math.abs(amount);
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 transition-colors" data-testid={testId}>
      <p className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-1">{title}</p>
      <p className={`text-2xl font-bold mb-1 ${over ? 'text-orange-600 dark:text-orange-400' : 'text-gray-900 dark:text-white'}`}>
        {over
          ? `$${absAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} over allocated`
          : `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
      </p>
      <p className="text-xs font-semibold text-gray-800 dark:text-gray-200">{description}</p>
      {over && (
        <p className="text-[11px] text-orange-700 dark:text-orange-300 mt-1">
          This account currently exceeds its target allocation.
        </p>
      )}
    </div>
  );
}

function AllocationBar({ label, amount, color, testId }) {
  const colorConfig = {
    red: {
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-700',
      text: 'text-red-700 dark:text-red-400',
      icon: 'text-red-500 dark:text-red-400'
    },
    blue: {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-700',
      text: 'text-blue-700 dark:text-blue-400',
      icon: 'text-blue-500 dark:text-blue-400'
    },
    green: {
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-700',
      text: 'text-green-700 dark:text-green-400',
      icon: 'text-green-500 dark:text-green-400'
    },
    purple: {
      bg: 'bg-purple-50 dark:bg-purple-900/20',
      border: 'border-purple-200 dark:border-purple-700',
      text: 'text-purple-700 dark:text-purple-400',
      icon: 'text-purple-500 dark:text-purple-400'
    },
    yellow: {
      bg: 'bg-yellow-50 dark:bg-yellow-900/20',
      border: 'border-yellow-200 dark:border-yellow-700',
      text: 'text-yellow-700 dark:text-yellow-400',
      icon: 'text-yellow-500 dark:text-yellow-400'
    },
    teal: {
      bg: 'bg-teal-50 dark:bg-teal-900/20',
      border: 'border-teal-200 dark:border-teal-700',
      text: 'text-teal-700 dark:text-teal-400',
      icon: 'text-teal-500 dark:text-teal-400'
    }
  };

  const config = colorConfig[color] || colorConfig.green;

  return (
    <div 
      className={`${config.bg} ${config.border} border-2 rounded-lg p-4 transition-all hover:shadow-md`}
      data-testid={testId}
    >
      <div className="flex flex-col h-full">
        <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2">{label}</p>
        <p className={`text-2xl font-bold ${config.text} mt-auto`}>
          ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </p>
      </div>
    </div>
  );
}

/**
 * Personal Account allocation card with a bills/surplus breakdown.
 * "Net After Expenses" is NOT a separate bucket — it lives INSIDE Personal.
 * This card makes that relationship visible so the five-card layout doesn't
 * look like it double-counts money.
 */
function PersonalAllocationCard({ label, total, surplus, testId }) {
  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const t = Number(total || 0);
  const s = Number(surplus || 0);
  const billsCovered = Math.max(0, t - s);
  const surplusPct = t > 0 ? Math.min(100, Math.max(0, (s / t) * 100)) : 0;

  return (
    <div
      className="bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-700 rounded-lg p-4 transition-all hover:shadow-md"
      data-testid={testId}
    >
      <div className="flex flex-col h-full">
        <p className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2">{label}</p>
        <p className="text-2xl font-bold text-green-700 dark:text-green-400">
          ${fmt(t)}
        </p>

        {/* Breakdown bar: bills covered (left) + surplus (right) */}
        <div className="mt-3 h-1.5 w-full rounded-full bg-green-200/60 dark:bg-green-800/40 overflow-hidden">
          <div
            className="h-full bg-green-600 dark:bg-green-400"
            style={{ width: `${100 - surplusPct}%`, float: 'left' }}
          />
          <div
            className="h-full bg-emerald-400 dark:bg-emerald-300"
            style={{ width: `${surplusPct}%`, float: 'left' }}
          />
        </div>

        <div className="mt-2 space-y-0.5">
          <div className="flex items-center justify-between text-[11px] text-gray-600 dark:text-gray-300">
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-sm bg-green-600 dark:bg-green-400" />
              Bills Covered
            </span>
            <span className="font-semibold">${fmt(billsCovered)}</span>
          </div>
          <div className="flex items-center justify-between text-[11px] text-gray-600 dark:text-gray-300">
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-sm bg-emerald-400 dark:bg-emerald-300" />
              Surplus (kept in Personal)
            </span>
            <span className="font-semibold">${fmt(s)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Weekly Target card with 3 distinct modes to avoid mixing survival, growth,
 * and historical catch-up into a single impossible number.
 */
function WeeklyTargetCard({ data, mode, onModeChange }) {
  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmt0 = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

  const modes = [
    { key: 'survival', label: 'Survival',     tone: 'bg-emerald-500' },
    { key: 'growth',   label: 'True Minimum', tone: 'bg-blue-500' },
    { key: 'catchup',  label: 'Catch-Up',     tone: 'bg-orange-500' },
  ];

  const current = data.targets?.[mode];
  // Income-pace signals — the canonical "are you keeping up?" numbers.
  const pace = data.income_pace || {};
  const behind = Number(pace.behind_amount || 0);
  const ahead  = Number(pace.ahead_amount  || 0);
  const status = pace.status || 'on_pace';
  const hasDeficit = behind > 0;
  const paceBand = data.suggested_catchup_pace;

  // Pace pct — compares actual weekly avg (3-stream: advances + overrides +
  // monthly checks; already aggregated server-side into actual_income_ytd)
  // against the True Minimum Income target. Pure UI math, no engine touch.
  const trueMinimum = Number(data.targets?.growth?.weekly_target || data.targets?.growth?.amount || 0);
  const weeksElapsed = Math.max(1, Number(pace.weeks_elapsed || 0));
  const currentWeeklyAvg = (Number(pace.actual_income_ytd || 0)) / weeksElapsed;
  const pacePct = trueMinimum > 0
    ? Math.round(((currentWeeklyAvg - trueMinimum) / trueMinimum) * 100)
    : null;
  let paceLabel, paceTone;
  if (pacePct === null) {
    paceLabel = 'No target set';
    paceTone = 'bg-white/15 text-white/90';
  } else if (pacePct >= 5) {
    paceLabel = `+${pacePct}% ahead of target`;
    paceTone = 'bg-emerald-300/30 text-white';
  } else if (pacePct <= -5) {
    paceLabel = `${Math.abs(pacePct)}% behind target`;
    paceTone = 'bg-rose-300/30 text-white';
  } else {
    paceLabel = 'On pace';
    paceTone = 'bg-amber-300/25 text-white';
  }

  // Catch-Up number — translates the pace % into a concrete weekly $ gap.
  // Same inputs as the pace pill, no new backend call.
  const weeklyGap = trueMinimum > 0 ? trueMinimum - currentWeeklyAvg : null;
  const gapAbs = Math.round(Math.abs(weeklyGap || 0));
  let gapText, gapToneText;
  if (weeklyGap === null) {
    gapText = 'Set a target to see your weekly gap';
    gapToneText = 'text-white/70';
  } else if (Math.abs(weeklyGap) < 1) {
    gapText = "You're on pace";
    gapToneText = 'text-amber-100';
  } else if (weeklyGap > 0) {
    gapText = `You need +$${gapAbs.toLocaleString()}/week to hit target`;
    gapToneText = 'text-rose-100';
  } else {
    gapText = `You're +$${gapAbs.toLocaleString()}/week above target`;
    gapToneText = 'text-emerald-100';
  }

  // Tone per mode
  const bgByMode = {
    survival: 'from-emerald-600 to-emerald-700',
    growth:   'from-blue-600 to-blue-700',
    catchup:  'from-orange-500 to-orange-600',
  };

  return (
    <div
      className={`rounded-lg shadow-lg p-6 text-white bg-gradient-to-br ${bgByMode[mode] || bgByMode.survival}`}
      data-testid="required-income-widget"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <Target className="w-8 h-8" />
          <h3 className="text-lg font-bold">Weekly Target</h3>
        </div>
        {status === 'behind' && (
          <span
            className="bg-white/20 text-xs font-bold px-2 py-1 rounded"
            data-testid="target-pace-pill"
            data-pace-status="behind"
            title={`Expected $${fmt0(pace.expected_income_ytd)} by week ${pace.weeks_elapsed}; you've earned $${fmt0(pace.actual_income_ytd)}.`}
          >
            BEHIND ${fmt0(behind)}
          </span>
        )}
        {status === 'ahead' && (
          <span
            className="bg-emerald-300/30 text-xs font-bold px-2 py-1 rounded"
            data-testid="target-pace-pill"
            data-pace-status="ahead"
            title={`You've earned $${fmt0(pace.actual_income_ytd)} vs pace of $${fmt0(pace.expected_income_ytd)} by week ${pace.weeks_elapsed}.`}
          >
            AHEAD ${fmt0(ahead)}
          </span>
        )}
        {status === 'on_pace' && (
          <span
            className="bg-white/10 text-xs font-bold px-2 py-1 rounded"
            data-testid="target-pace-pill"
            data-pace-status="on_pace"
            title="Your income matches the pace required to keep the system stable."
          >
            ON PACE
          </span>
        )}
      </div>

      {/* Mode toggle */}
      <div className="inline-flex bg-white/15 backdrop-blur rounded-lg p-1 mb-4" data-testid="target-mode-toggle">
        {modes.map((m) => {
          const active = mode === m.key;
          const disabled = m.key === 'catchup' && !hasDeficit;
          return (
            <button
              key={m.key}
              onClick={() => !disabled && onModeChange(m.key)}
              disabled={disabled}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                active
                  ? 'bg-white text-gray-900 shadow-sm'
                  : disabled
                    ? 'text-white/40 cursor-not-allowed'
                    : 'text-white/90 hover:bg-white/10'
              }`}
              data-testid={`target-mode-${m.key}`}
              title={disabled ? 'No catch-up needed — you\'re on pace' : ''}
            >
              {m.label}
            </button>
          );
        })}
      </div>

      <div className="text-4xl font-bold mb-1" data-testid="target-headline-amount">
        ${fmt(current?.amount)}
      </div>
      <p className="text-white/80 text-xs uppercase tracking-wide mb-2">
        {mode === 'survival' && 'Weekly Operating Target'}
        {mode === 'growth' && 'True Minimum Income'}
        {mode === 'catchup' && (hasDeficit ? `You're behind by $${fmt0(behind)}` : `You're on pace`)}
      </p>
      {/* Pace % vs True Minimum — quick "ahead/behind/on pace" read. */}
      <div
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold mb-1 ${paceTone}`}
        data-testid="target-pace-pct"
        data-pace-pct={pacePct === null ? '' : pacePct}
        title="Compares your average weekly income against your True Minimum Income."
      >
        {paceLabel}
      </div>
      {/* Catch-Up number — the actionable $ translation of the pace pill. */}
      <p
        className={`text-sm font-medium mb-2 ${gapToneText}`}
        data-testid="target-weekly-gap"
        data-gap-direction={
          weeklyGap === null
            ? 'none'
            : Math.abs(weeklyGap) < 1
            ? 'on_pace'
            : weeklyGap > 0
            ? 'behind'
            : 'ahead'
        }
      >
        {gapText}
      </p>
      <p className="text-white/90 text-sm mb-2">
        {mode === 'survival' && 'Covers your essential bills this week.'}
        {mode === 'growth' && 'Minimum weekly income to keep your plan fully funded — matches your Financial Plan → True Minimum Income.'}
        {mode === 'catchup' && hasDeficit && `Suggested recovery: +$${fmt(pace.catchup_per_week)}/week over ${pace.catchup_weeks} weeks.`}
        {mode === 'catchup' && !hasDeficit && 'No catch-up needed — you\'re on pace.'}
      </p>
      <p className="text-white/70 text-xs mb-4">
        This compares your income to the amount needed to keep your system on track.
      </p>

      {/* Mode-specific detail */}
      {mode === 'catchup' && hasDeficit && (
        <div className="bg-black/20 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-white/80">Behind (income pace)</span>
            <span className="font-bold" data-testid="catchup-behind-amount">${fmt0(behind)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-white/80">Recovery window</span>
            <span className="font-bold">{pace.catchup_weeks} weeks</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-white/80">Extra per week</span>
            <span className="font-bold">+${fmt(pace.catchup_per_week)}</span>
          </div>
          {paceBand && paceBand.range_label && paceBand.range_label !== '—' && (
            <div className="pt-2 border-t border-white/15 text-xs text-white/80">
              💡 Pace band: <span className="font-semibold text-white">{paceBand.range_label}</span>
            </div>
          )}
          <div className="pt-2 border-t border-white/15 text-xs text-white/80">
            You don&apos;t need to fix everything at once. Stay consistent and close the gap over time.
          </div>
        </div>
      )}

      {mode === 'survival' && (
        <div className="bg-black/20 rounded-lg p-3 text-xs text-white/80">
          Hit this number and your system stays stable. Switch to <strong className="text-white">True Minimum</strong> to see the income needed to keep your full plan funded.
        </div>
      )}

      {mode === 'growth' && (
        <div className="bg-black/20 rounded-lg p-3 text-xs text-white/80">
          This is the canonical Financial Plan number — hit it weekly and every bucket (Personal, Business, Taxes, Savings) stays on target.
          {hasDeficit && ' Switch to Catch-Up to also close your income-pace gap.'}
        </div>
      )}
    </div>
  );
}

export default Dashboard;
